import type { Ticket, TicketInput, ParsedTicket, Stats } from '@/types'

const BASE = '/api'

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  }
  const token = localStorage.getItem('tt_token')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(BASE + path, {
    ...options,
    headers,
  })

  if (res.status === 401) {
    localStorage.removeItem('tt_token')
    if (location.pathname !== '/login') {
      location.href = '/login'
    }
    throw new ApiError(401, '未登录')
  }

  if (!res.ok) {
    let msg = `${res.status}`
    try {
      const data = (await res.json()) as { error?: string }
      if (data?.error) msg = data.error
    } catch {}
    throw new ApiError(res.status, msg)
  }

  return res.json() as Promise<T>
}

export const api = {
  // 认证
  login(password: string): Promise<{ ok: true; token: string }> {
    return request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  },

  // 票记录
  listTickets(params: {
    startDate?: string
    endDate?: string
    departure?: string
    arrival?: string
    trainNo?: string
  } = {}): Promise<{ tickets: Ticket[] }> {
    const q = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') q.set(k, v)
    })
    const qs = q.toString()
    return request(`/tickets${qs ? '?' + qs : ''}`)
  },

  createTicket(input: TicketInput): Promise<{ ticket: Ticket }> {
    return request('/tickets', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  updateTicket(id: number, input: Partial<TicketInput>): Promise<{ ticket: Ticket }> {
    return request(`/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },

  deleteTicket(id: number): Promise<{ ok: true }> {
    return request(`/tickets/${id}`, { method: 'DELETE' })
  },

  // OCR
  ocrImage(imageBase64: string): Promise<{ parsed: ParsedTicket; raw_text: string }> {
    return request('/ocr', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    })
  },

  parseText(text: string): Promise<{ parsed: ParsedTicket }> {
    return request('/parse', {
      method: 'POST',
      body: JSON.stringify({ text }),
    })
  },

  // 统计
  getStats(): Promise<Stats> {
    return request('/stats')
  },

  // 导入导出
  exportCsv(): Promise<Blob> {
    return fetch(BASE + '/export', {
      headers: { Authorization: `Bearer ${localStorage.getItem('tt_token') || ''}` },
    }).then((r) => {
      if (!r.ok) throw new ApiError(r.status, '导出失败')
      return r.blob()
    })
  },

  importCsv(csv: string): Promise<{ inserted: number; skipped: number }> {
    return request('/import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    })
  },
}

export { ApiError }